import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API_BASE_URL } from "../config.tsx";
import { getPasswordErrors } from "./helpers/helpers.tsx";
import apiFetch from "./helpers/requests";
import { useTranslation } from "react-i18next";

import "../App.css";

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [resetPasswordError, setResetPasswordError] = useState(false);
  const [resetPasswordSendEmailSuccess, setResetPasswordSendEmailSuccess] =
    useState(false);
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState(false);
  const [isResetLink, setIsResetLink] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const { t, i18n } = useTranslation();

  const mode = searchParams.get("mode");
  const oobCode = searchParams.get("oobCode");

  useEffect(() => {
    if (mode === "resetPassword" && oobCode) {
      setIsResetLink(true);
    }
  }, [mode, oobCode]);

  const resetPassword = async (email) => {
    try {
      const result = await apiFetch(`/resetPassword`, {
        method: "POST",
        body: JSON.stringify({ email }),
      }, true);
      return result;
    } catch (error) {
      console.error("Error while reset password:", error);
      throw new Error("An error occurred while reset password");
    }
  };

  const updatePassword = async (newPassword: string) => {
    try {
      const result = await apiFetch(`/updatePassword`, {
        method: "POST",
        body: JSON.stringify({ oobCode, newPassword }),
      }, true);
      if (result.success) {
        setResetPasswordSuccess(true);
        setResetPasswordError(false);
      } else {
        setResetPasswordError(true);
        setResetPasswordSuccess(false);
      }
    } catch (error) {
      console.error("Error while updating password:", error);
      setResetPasswordError(true);
      setResetPasswordSuccess(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setFormSubmitted(true);

    if (!isEmailValid()) {
      return;
    }

    const result = await resetPassword(email);
    result
      ? setResetPasswordSendEmailSuccess(true)
      : setResetPasswordError(true);
  };

  const handleNewPasswordSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();
    setFormSubmitted(true);

    if (getPasswordErrors(newPassword, confirmPassword, t)) {
      return;
    }

    if (newPassword !== confirmPassword) {
      setResetPasswordError(true);
      return;
    }
    await updatePassword(newPassword);
  };

  const handleBackClick = () => {
    navigate("/login");
  };

  const isEmailValid = () => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const renderResetPasswordSuccess = () => {
    return (
      <>
        {resetPasswordSuccess && (
          <div
            style={{
              textAlign: "center",
              marginTop: "2vh",
              fontSize: "1.8vh",
            }}
          >
            {t("Success")}
          </div>
        )}
        <div
          style={{
            textAlign: "center",
            marginTop: "2vh",
            fontSize: "1.2vh",
            fontFamily: "VirtualLabQuickSandMedium",
            fontWeight: "bold",
          }}
        >
          {resetPasswordSuccess ? (
            <>{t("Your_password_has_been_successfully_updated")}</>
          ) : (
            <>
              {t("An_email_has_been_sent_to")} {email}{" "}
              {t("with_instructions_on_how_to")}
              {t("finish_resetting_your_password")}
            </>
          )}
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            marginTop: "3vh",
          }}
        >
          <button
            type="button"
            className="btn btn-primary mb-2"
            onClick={handleBackClick}
            style={{
              width: "45%",
              height: "5vh",
              maxHeight: "40px",
              maxWidth: "100%",
              borderRadius: "20px",
              marginTop: "10px",
              backgroundColor: "#39A686",
              borderColor: "#39A686",
              fontSize: "min(1.1vh, 3.2vw)",
              paddingLeft: "10px",
              paddingRight: "10px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            {t("Return_to_login")}
          </button>
        </div>
      </>
    );
  };

  const renderResetPasswordError = () => {
    return (
      <>
        <div
          style={{
            textAlign: "center",
            margin: "7vh 0",
            fontSize: "1.2vh",
            fontFamily: "VirtualLabQuickSandMedium",
            fontWeight: "bold",
          }}
        >
          {t("There_was_an_unspecified_error_Please_try_again")}
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            marginTop: "3vh",
          }}
        >
          <button
            type="button"
            className="btn btn-primary mb-2"
            onClick={handleBackClick}
            style={{
              width: "45%",
              height: "5vh",
              maxHeight: "40px",
              maxWidth: "100%",
              borderRadius: "20px",
              marginTop: "10px",
              backgroundColor: "#39A686",
              borderColor: "#39A686",
              fontSize: "min(1.1vh, 3.2vw)",
              paddingLeft: "10px",
              paddingRight: "10px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            {t("Return_to_login")}
          </button>
        </div>
      </>
    );
  };

  const renderEnterNewPassword = () => {
    return (
      <>
        <form onSubmit={handleNewPasswordSubmit} className="m-4">
          <div
            style={{
              textAlign: "center",
              marginTop: "2vh",
              marginBottom: "2vh",
              fontSize: "1.8vh",
            }}
          >
            {t("Enter_New_Password")}
          </div>
          <input
            type="password"
            placeholder={t("New_Password")}
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              setFormSubmitted(false);
            }}
            className="form-control mb-3"
            required
            style={{
              backgroundColor: "#CCCCCC",
              borderRadius: "20px",
              color: "#404040",
              height: "5vh",
              maxHeight: "40px",
              fontSize: "1.2vh",
              paddingLeft: "2vh",
            }}
          />
          <input
            type="password"
            placeholder={t("Confirm_Password")}
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              setFormSubmitted(false);
            }}
            className="form-control mb-3"
            required
            style={{
              backgroundColor: "#CCCCCC",
              borderRadius: "20px",
              color: "#404040",
              height: "5vh",
              maxHeight: "40px",
              fontSize: "1.2vh",
              paddingLeft: "2vh",
            }}
          />
          <div>
            <label
              htmlFor="passwordInput"
              style={{
                color: "#A63939",
                fontSize: "0.8em",
                visibility:
                  formSubmitted &&
                  getPasswordErrors(newPassword, confirmPassword, t)
                    ? "visible"
                    : "hidden",
              }}
            >
              {getPasswordErrors(newPassword, confirmPassword, t)}
            </label>
          </div>
          <button
            type="submit"
            className="btn btn-primary mb-2"
            style={{
              width: "45%",
              height: "5vh",
              maxHeight: "40px",
              maxWidth: "100%",
              borderRadius: "20px",
              marginTop: "10px",
              backgroundColor: "#39A686",
              borderColor: "#39A686",
              fontSize: "1.2vh",
            }}
            disabled={formSubmitted}
          >
            {t("Update_Password")}
          </button>
        </form>
      </>
    );
  };

  const renderResetPasswordForm = () => {
    return (
      <>
        <div
          style={{
            textAlign: "center",
            marginTop: "2vh",
            fontSize: "1.8vh",
          }}
        >
          {t("Reset_your_password")}
        </div>
        <form onSubmit={handleSubmit} className="m-4">
          <div className="mb-3" style={{ marginTop: "3vh", textAlign: "left" }}>
            <label
              htmlFor="emailInput"
              style={{
                marginLeft: "2vh",
                color: "#404040",
                fontSize: "1.2vh",
              }}
            >
              {t("Email")}
            </label>
            <input
              className="form-control"
              id="emailInput"
              placeholder="example@site.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setFormSubmitted(false);
              }}
              style={{
                backgroundColor: "#CCCCCC",
                borderRadius: "20px",
                color: "#404040",
                height: "5vh",
                maxHeight: "40px",
                fontSize: "1.2vh",
                paddingLeft: "2vh",
              }}
            />
            <label
              htmlFor="emailInput"
              style={{
                marginLeft: "20px",
                color: "#A63939",
                fontSize: "0.8em",
                visibility:
                  formSubmitted && !isEmailValid() ? "visible" : "hidden",
              }}
            >
              {t("This_email_is_invalid")}
            </label>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              marginTop: "3vh",
            }}
          >
            <button
              type="submit"
              className="btn btn-primary mb-2"
              style={{
                width: "45%",
                height: "5vh",
                maxHeight: "40px",
                maxWidth: "100%",
                borderRadius: "20px",
                marginTop: "10px",
                backgroundColor: "#39A686",
                borderColor: "#39A686",
                fontSize: "1.1vh",
              }}
              disabled={formSubmitted}
            >
              {t("Reset_password")}
            </button>
            <button
              type="button"
              className="btn btn-primary mb-2"
              onClick={handleBackClick}
              style={{
                width: "60%",
                maxWidth: "100%",
                borderRadius: "20px",
                marginTop: "10px",
                background: "none",
                borderColor: "transparent",
                color: "#404040",
                fontSize: "1.2vh",
              }}
            >
              {t("Back_to_login")}
            </button>
          </div>
        </form>
      </>
    );
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "calc(100vh - 40px)",
        paddingTop: "40px",
        maxWidth: "1400px",
        margin: "0 auto",
      }}
    >
      <div className="m-4" style={{ minHeight: "50vh", textAlign: "center" }}>
        <img
          src="/images/VR_ICU_Logo_Main.png"
          alt="Logo"
          className="mb-3"
          style={{
            width: "36.7vh",
          }}
        />
        {resetPasswordSuccess || resetPasswordSendEmailSuccess
          ? renderResetPasswordSuccess()
          : resetPasswordError
          ? renderResetPasswordError()
          : isResetLink
          ? renderEnterNewPassword()
          : renderResetPasswordForm()}
      </div>
    </div>
  );
};

export default ResetPassword;
